/**
 * Type definitions for the ANNA AI Studio
 */

export enum DocumentType {
  VB_MAPP = "vb-mapp", // Combined VB-MAPP (Milestones + Barriers)
  PARENT_INTERVIEW = "parent-interview",
  VINELAND_3 = "vineland-3",
  DIAGNOSTIC_REPORT = "diagnostic-report",
  IEP = "iep",
  INTAKE_FORM = "intake-form",
  PREVIOUS_ABA_PLAN = "previous-aba-plan",
  OBSERVATION_NOTE = "observation-note",
  QABF = "qabf",
}

// Document type configurations with display metadata
export interface DocumentTypeDisplay {
  type: DocumentType;
  name: string;
  icon: string;
  color: string;
  description: string;
}

export const DOCUMENT_TYPES: DocumentTypeDisplay[] = [
  {
    type: DocumentType.VB_MAPP,
    name: "VB-MAPP Assessment",
    icon: "📊",
    color: "#2563eb",
    description: "Analyze VB-MAPP Milestones and Barriers scores",
  },
  {
    type: DocumentType.PARENT_INTERVIEW,
    name: "Parent Interview",
    icon: "👥",
    color: "#10b981",
    description: "Extract information from parent interview forms",
  },
  {
    type: DocumentType.VINELAND_3,
    name: "Vineland-3 Report",
    icon: "📈",
    color: "#8b5cf6",
    description: "Analyze Vineland-3 adaptive behavior scales",
  },
  {
    type: DocumentType.DIAGNOSTIC_REPORT,
    name: "Diagnostic Report",
    icon: "🏥",
    color: "#ef4444",
    description: "Extract information from diagnostic evaluations",
  },
  {
    type: DocumentType.IEP,
    name: "IEP Document",
    icon: "📋",
    color: "#f59e0b",
    description: "Extract information from Individualized Education Programs",
  },
  {
    type: DocumentType.INTAKE_FORM,
    name: "Intake Form",
    icon: "📝",
    color: "#06b6d4",
    description: "Extract information from ABA services intake forms",
  },
  {
    type: DocumentType.PREVIOUS_ABA_PLAN,
    name: "Previous ABA Plan",
    icon: "📑",
    color: "#ec4899",
    description: "Extract information from prior ABA treatment plans",
  },
  {
    type: DocumentType.OBSERVATION_NOTE,
    name: "Observation Note",
    icon: "👁️",
    color: "#84cc16",
    description: "Extract information from clinical observation notes",
  },
  {
    type: DocumentType.QABF,
    name: "QABF Assessment",
    icon: "📋",
    color: "#7c3aed",
    description: "Analyze Questions About Behavioral Function assessments",
  },
];

// Helper to get document type name by type value
export const getDocumentTypeName = (type: string): string => {
  return DOCUMENT_TYPES.find((dt) => dt.type === type)?.name || type;
};

export interface ModelInfo {
  id: string;
  name: string;
  provider: string;
  description?: string;
}

export interface FileMetadata {
  filename: string;
  file_size: number;
  page_count?: number;
  upload_time: string;
  mime_type: string;
}

export interface DocumentAnalysisResponse {
  success: boolean;
  data: Record<string, any>;
  document_type: DocumentType;
  model_used: string;
  processing_time: number;
  metadata: FileMetadata;
  confidence_score?: number;
}

export interface DocumentTypeInfo {
  id: string;
  name: string;
  description: string;
  color: string;
}

export interface ProcessingStatus {
  status:
    | "idle"
    | "uploading"
    | "validating"
    | "processing"
    | "parsing"
    | "completed"
    | "error";
  message: string;
  progress: number;
  processing_time?: number;
}

export interface DocumentTypeConfig {
  system_prompt: string;
  user_prompt: string;
  output_schema: Record<string, any>;
  default_model: string;
}

export interface InputPanelState {
  file: File | null;
  selectedModel: string;
  systemPrompt: string;
  userPrompt: string;
  outputSchema: Record<string, any>;
  showPreview: boolean;
  showAdvancedOptions?: boolean;
  excelPreview?: ExcelPreviewData;
}

export interface ExcelPreviewData {
  selected_sheet: string | null;
  available_sheets: string[];
  preview: string | null;
  error: string | null;
  loadingPreview?: boolean;
}

export interface PDFPreviewData {
  pdf_data: string | null;
  error: string | null;
}

export interface RAGChunk {
  content: string;
  source: string;
  page_number?: number;
  score: number;
  chunk_id: string;
}

export interface SectionGenerationState {
  selectedModel: string;
  systemPrompt: string;
  userPrompt: string;
  selectedReferences: string[]; // Multiple reference selection
  generatedContent: any | null;
  isGenerating: boolean;
  lastGeneratedAt: string | null;
  lastGeneratedModel: string | null;
  hasUnsavedChanges: boolean;
  showAdvancedOptions?: boolean;
  previewState?: {
    isActive: boolean;
    title: string;
    data: any;
    originalDocument?: File | null;
    type: "analysis" | "section";
    sectionId?: string | null;
  };
  // RAG configuration
  ragEnabled?: boolean;
  ragNumChunks?: number;
  ragMinScore?: number;
  ragChunks?: RAGChunk[];
  showRagChunks?: boolean;
}

// Treatment Plan Session types (for unified wizard)
export enum WizardStep {
  UPLOAD_DOCUMENTS = 1,
  PATIENT_DATA = 2,
  ASSESSMENT_RESULTS = 3,
  TREATMENT_GOALS = 4,
  SUPPORT_PLAN = 5,
  REVIEW = 6,
}

// Step labels for display
export const WIZARD_STEP_LABELS: Record<WizardStep, string> = {
  [WizardStep.UPLOAD_DOCUMENTS]: "Upload Documents",
  [WizardStep.PATIENT_DATA]: "Patient Data",
  [WizardStep.ASSESSMENT_RESULTS]: "Assessment Results",
  [WizardStep.TREATMENT_GOALS]: "Treatment Goals",
  [WizardStep.SUPPORT_PLAN]: "Support Plan",
  [WizardStep.REVIEW]: "Review",
};

export interface UploadedFileInfo {
  s3_key: string;
  filename: string;
  file_size: number;
  mime_type: string;
}

export enum SessionStatus {
  DRAFT = "draft",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  ARCHIVED = "archived",
}

export interface TreatmentPlanSession {
  id: number;
  client_name: string;
  date_of_birth: string | null;
  status: SessionStatus | string;
  current_step: number;
  analyzed_documents: Record<string, DocumentAnalysisResponse | null>;
  generated_sections: Record<string, any>;
  section_states: Record<string, SectionGenerationState>;
  input_states: Record<string, InputPanelState>;
  uploaded_files: Record<string, UploadedFileInfo>;
  created_at: string;
  updated_at: string | null;
}

export interface TreatmentPlanSessionListItem {
  id: number;
  client_name: string;
  date_of_birth: string | null;
  status: SessionStatus | string;
  current_step: number;
  created_at: string;
  updated_at: string | null;
}

export interface ClientSuggestion {
  client_name: string;
  date_of_birth: string | null;
  session_count: number;
}
